import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { useScrollTop } from '@alycecom/hooks';

import PageWithDataWrapper from '../../../../components/PageWithDataWrapper';
import StyledHeader from '../StyledHeader/StyledHeader';
import GiftStatusBar from '../../../../components/GiftStatusBar/GiftStatusBar';
import {
  getGiftHashId,
  getGiftGl,
  getIsLoading as getIsDataLoading,
  getGiftProduct,
  getIsGiftClaimed,
} from '../../store/data/data.selectors';
import { getIsLoading, getIsMessageSent, getDelayedGiftCard } from '../../store/thanks/thanks.selectors';
import { sendThanksRequest, thanksStatusRequest, pollThanksRedeemStatus } from '../../store/thanks/thanks.actions';
import useGiftStatuses from '../../hooks/useGiftStatuses';
import { GIFT_FLOW_THANKS_ID } from '../../../../constants/giftFlow.constants';
import useResponsive from '../../../../hooks/useResponsive';
import Footer from '../../../../components/Shared/Footer';
import usePageLoadEvent from '../../../../hooks/usePageLoadEvent';
import { getStatuses } from '../../store/statuses/statuses.selectors';
import { getIsGiftDataLoaded, getRecipientEmail } from '../../store/recipient.selectors';
import { dataLoadRequest } from '../../store/data/data.actions';

import ThankYouSkeleton from './ThankYouContent/ThankYouSkeleton';
import { CongratulationTitle, ThankYouForm, ThankYouSentTitle } from './ThankYouContent';
import GiftCardProcessing from './GiftCardProcessing/GiftCardProcessing';
import DelayedGiftCardPreview from './GiftCardProcessing/DelayedGiftCardPreview';

const ThankYouPage = (): JSX.Element => {
  const dispatch = useDispatch();
  const { isMobile } = useResponsive();

  const isGiftDataLoaded = useSelector(getIsGiftDataLoaded);
  const hashId = useSelector(getGiftHashId);
  const gl = useSelector(getGiftGl);

  const isMessageSent = useSelector(getIsMessageSent);
  const isDelayedGiftCard = useSelector(getDelayedGiftCard);
  const claimedProduct = useSelector(getGiftProduct);
  const isLoading = useSelector(getIsLoading);
  const isDataLoading = useSelector(getIsDataLoading);
  const statusFlags = useSelector(getStatuses);
  const isClaimed = useSelector(getIsGiftClaimed);

  const isLoaderVisible = isLoading || isDataLoading;
  const recipientEmail = useSelector(getRecipientEmail);
  const isDonation = claimedProduct && claimedProduct.isDonation;
  const isAutomatedGiftCard = claimedProduct && claimedProduct.isTangoCard;
  const congratulationTitle = isAutomatedGiftCard ? 'Hooray! Your gift card is now processing' : 'Hooray!';
  const isFormYouView = !isLoaderVisible && !isMessageSent;
  const isMessageSentView = !isLoaderVisible && isMessageSent && !isDelayedGiftCard;
  const isGiftCardProcessingView = !isLoaderVisible && isAutomatedGiftCard && !isDelayedGiftCard;
  const isDelayedGiftCardView = !isLoaderVisible && isAutomatedGiftCard && isDelayedGiftCard;

  const wrapperMarginTop = statusFlags.preview ? [10, 5] : [];

  const handleSendMessage = useCallback(
    message => {
      if (!statusFlags.preview) {
        dispatch(sendThanksRequest({ hashId, message }));
      }
    },
    [dispatch, hashId, statusFlags],
  );

  const { statuses, activeStatus, setActiveStatus } = useGiftStatuses();

  useScrollTop();

  usePageLoadEvent('Thank You Page - Opened');

  useEffect(() => {
    if (hashId) {
      dispatch(thanksStatusRequest({ hashId }));
    }
  }, [dispatch, hashId]);

  useEffect(() => {
    if (hashId && isAutomatedGiftCard) {
      dispatch(pollThanksRedeemStatus.pending({ hashId }));
    }
  }, [dispatch, isAutomatedGiftCard, hashId]);

  useEffect(() => {
    if (hashId && !isClaimed) {
      dispatch(dataLoadRequest({ hashId }));
    }
  }, [dispatch, hashId, isClaimed]);

  useEffect(() => {
    setActiveStatus(GIFT_FLOW_THANKS_ID);
  }, [setActiveStatus]);

  return (
    <PageWithDataWrapper isLoaded={isGiftDataLoaded}>
      <Box bgcolor="common.white" mt={wrapperMarginTop}>
        <StyledHeader>
          <GiftStatusBar statuses={statuses} activeStatus={activeStatus} showStatuses={!isMobile} />
        </StyledHeader>
        <Box width={1} height={['calc(100vh - 56px)', 'calc(100vh - 80px)']} display="flex" flexDirection="column">
          {isLoaderVisible && <ThankYouSkeleton />}
          {isMessageSentView && <ThankYouSentTitle recipientEmail={recipientEmail} isDonation={isDonation} gl={gl} />}
          {isFormYouView && (
            <CongratulationTitle
              recipientEmail={recipientEmail}
              title={congratulationTitle}
              gl={gl}
              isGiftCard={isAutomatedGiftCard}
            />
          )}
          {isGiftCardProcessingView && <GiftCardProcessing />}
          {isDelayedGiftCardView && <DelayedGiftCardPreview product={claimedProduct} />}
          {isFormYouView && (
            <Box
              height={1}
              px={[2, 'auto']}
              mt={[2, 6]}
              bgcolor="customStyling.backgroundColor"
              display="flex"
              flexDirection="column"
              justifyContent={['center', 'space-between']}
              alignItems="center"
            >
              <ThankYouForm onSubmit={handleSendMessage} />
              <Footer textColor="common.white" mobileHidden />
            </Box>
          )}
        </Box>
      </Box>
    </PageWithDataWrapper>
  );
};

export default ThankYouPage;
